import "./App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./lib/auth";

import PublicLayout from "./layouts/PublicLayout";
import AdminLayout from "./layouts/AdminLayout";

const Home = lazy(() => import("./pages/Home"));
const Investments = lazy(() => import("./pages/Investments"));
const InvestmentDetail = lazy(() => import("./pages/InvestmentDetail"));
const Centralizator = lazy(() => import("./pages/Centralizator"));
const MapPage = lazy(() => import("./pages/MapPage"));
const StaticPage = lazy(() => import("./pages/StaticPage"));
const FAQ = lazy(() => import("./pages/FAQ"));

const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminInvestments = lazy(() => import("./pages/admin/AdminInvestments"));
const AdminInvestmentForm = lazy(() => import("./pages/admin/AdminInvestmentForm"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminNeighborhoods = lazy(() => import("./pages/admin/AdminNeighborhoods"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminPages = lazy(() => import("./pages/admin/AdminPages"));
const AdminFAQs = lazy(() => import("./pages/admin/AdminFAQs"));
const AdminProfile = lazy(() => import("./pages/admin/AdminProfile"));
const AdminFrontendSeo = lazy(() => import("./pages/admin/AdminFrontendSeo"));
const AdminCookieConsent = lazy(() => import("./pages/admin/AdminCookieConsent"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const Forbidden = lazy(() => import("./pages/Forbidden"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));

function NotFound() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center">
      <div className="font-display text-7xl font-bold text-slate-200 mb-4">404</div>
      <h1 className="font-display text-3xl font-medium mb-3">Pagină inexistentă</h1>
      <p className="text-slate-600 mb-6">Adresa căutată nu există pe acest site.</p>
      <a href="/" className="inline-block px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-sm hover:bg-slate-700">Înapoi acasă</a>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-500">Se încarcă...</div>}>
          <Routes>
            {/* Admin login is outside the admin layout */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/forbidden" element={<Forbidden />} />

            {/* Admin protected */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="investments" element={<AdminInvestments />} />
              <Route path="investments/new" element={<AdminInvestmentForm />} />
              <Route path="investments/:id" element={<AdminInvestmentForm />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="neighborhoods" element={<AdminNeighborhoods />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="faqs" element={<AdminFAQs />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="frontend-seo" element={<AdminFrontendSeo />} />
              <Route path="cookie-consent" element={<AdminCookieConsent />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
            </Route>

            {/* Public */}
            <Route path="/" element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="investitii" element={<Investments />} />
              <Route path="investitii/:slug" element={<InvestmentDetail />} />
              <Route path="centralizator-investitii" element={<Centralizator />} />
              <Route path="harta-investitiilor" element={<MapPage />} />
              <Route path="faqs" element={<FAQ />} />
              <Route path="administratia-viziteu" element={<StaticPage slug="administratia-viziteu" />} />
              <Route path="politica-de-confidentialitate" element={<StaticPage slug="politica-de-confidentialitate" />} />
              <Route path=":slug" element={<StaticPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
